import PropTypes from "prop-types";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
// @mui
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// components
import Iconify from "src/components/iconify";
//
import {
  BOOLEAN_OPERATIONS,
  DATE_OPERATIONS,
  FILTER_ROW_DELAY,
  NUMBER_OPERATIONS,
  OPERATION_ICONS,
  OPERATION_LABELS,
  STRING_OPERATIONS,
} from "../constants";
import { formatValue, isDateType, toDate } from "../core/format";
import { cellClassName } from "./grid-rows";

function operationsFor(column) {
  if (column.filterOperations?.length) return column.filterOperations;
  if (column.dataType === "number") return NUMBER_OPERATIONS;
  if (isDateType(column.dataType)) return DATE_OPERATIONS;
  if (column.dataType === "boolean") return BOOLEAN_OPERATIONS;

  return STRING_OPERATIONS;
}

function toInputText(column, value, locale) {
  if (value == null || value === "") return "";
  if (column.dataType === "number")
    return String(value).replace(".", locale?.decimalSeparator || ".");

  return String(value);
}

function parseInput(column, text) {
  if (text == null || text.trim() === "") return null;

  if (column.dataType === "number") {
    const number = Number(text.trim().replace(",", "."));

    return Number.isNaN(number) ? undefined : number;
  }

  return text;
}

function DateEditor({ value, onChange, format, placeholder, delay = 0 }) {
  const external = toDate(value);
  const externalKey = external ? external.getTime() : null;
  const [local, setLocal] = useState(() => (external ? moment(external) : null));
  const timerRef = useRef(null);

  useEffect(() => {
    setLocal(current => {
      const currentKey = current && current.isValid() ? current.toDate().getTime() : null;

      if (currentKey === externalKey) return current;

      return externalKey == null ? null : moment(externalKey);
    });
  }, [externalKey]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const emit = next => {
    clearTimeout(timerRef.current);

    if (delay) timerRef.current = setTimeout(() => onChange(next), delay);
    else onChange(next);
  };

  return (
    <DatePicker
      value={local}
      onChange={next => {
        setLocal(next);

        if (next == null) {
          emit(null);
          return;
        }

        if (moment.isMoment(next) && next.isValid() && next.year() >= 1000) emit(next.toDate());
      }}
      format={format}
      slotProps={{
        textField: {
          variant: "standard",
          placeholder,
          InputProps: { disableUnderline: true },
          inputProps: { placeholder },
          sx: { flex: "1 1 auto", minWidth: 0, "& .MuiInputBase-input": { py: 0, px: 0.5 } },
        },
        openPickerButton: { size: "small", sx: { p: 0.25 } },
        openPickerIcon: { sx: { fontSize: 16 } },
      }}
    />
  );
}

DateEditor.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  delay: PropTypes.number,
  format: PropTypes.string,
  placeholder: PropTypes.string,
};

function BetweenEditor({ column, value, onApply, locale, t }) {
  const [anchor, setAnchor] = useState(null);
  const [range, setRange] = useState(() => (Array.isArray(value) ? value : [null, null]));
  const isDate = isDateType(column.dataType);

  useEffect(() => {
    setRange(Array.isArray(value) ? value : [null, null]);
  }, [value]);

  const label = Array.isArray(value)
    ? value
        .map(item =>
          item == null ? "…" : formatValue(isDate ? toDate(item) : item, column, locale)
        )
        .join(" – ")
    : "";

  const update = (index, next) =>
    setRange(current => current.map((item, i) => (i === index ? next : item)));

  return (
    <>
      <InputBase
        readOnly
        value={label}
        placeholder={t("gridOperationBetween")}
        onClick={event => setAnchor(event.currentTarget)}
        sx={{ flex: "1 1 auto", minWidth: 0, cursor: "pointer", "& input": { cursor: "pointer" } }}
      />
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { p: 2, width: 280 } } }}
      >
        <Stack spacing={2}>
          {[0, 1].map(index =>
            isDate ? (
              <DateEditor
                key={index}
                value={range[index]}
                onChange={next => update(index, next)}
                format={locale?.dateInputFormat}
                placeholder={index === 0 ? t("from") : t("to")}
              />
            ) : (
              <InputBase
                key={index}
                value={toInputText(column, range[index], locale)}
                placeholder={index === 0 ? t("from") : t("to")}
                onChange={event => update(index, parseInput(column, event.target.value) ?? null)}
                inputProps={{ inputMode: "decimal" }}
                sx={{
                  px: 1,
                  height: 36,
                  borderRadius: 1,
                  border: theme => `1px solid ${theme.palette.divider}`,
                }}
              />
            )
          )}
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
          >
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                onApply(null);
                setAnchor(null);
              }}
            >
              {t("clear")}
            </Button>
            <Button
              size="small"
              variant="soft"
              color="primary"
              onClick={() => {
                onApply(range.every(item => item == null) ? null : range);
                setAnchor(null);
              }}
            >
              {t("gridApply")}
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}

BetweenEditor.propTypes = {
  column: PropTypes.object,
  value: PropTypes.any,
  onApply: PropTypes.func,
  locale: PropTypes.object,
  t: PropTypes.func,
};

function FilterCell({ viewColumn, ctx }) {
  const { column } = viewColumn;
  const condition = ctx.getFilterRowCondition(column);
  const operations = operationsFor(column);
  const operation = condition?.operation || ctx.getSelectedOperation(column);
  const appliedValue = condition?.value ?? null;
  const [text, setText] = useState(() => toInputText(column, appliedValue, ctx.locale));
  const [menuAnchor, setMenuAnchor] = useState(null);
  const timerRef = useRef(null);
  const columnRef = useRef(column);
  const appliedKey = JSON.stringify(appliedValue ?? null);

  columnRef.current = column;

  useEffect(() => {
    const { current } = columnRef;

    if (isDateType(current.dataType) || current.dataType === "boolean") return;

    const value = JSON.parse(appliedKey);

    setText(previous => {
      const parsed = parseInput(current, previous);

      if ((parsed ?? null) === value) return previous;

      return toInputText(current, value, ctx.locale);
    });
  }, [appliedKey, ctx.locale]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const apply = (nextOperation, value) => {
    clearTimeout(timerRef.current);
    ctx.applyFilterRow(column, nextOperation, value);
  };

  const scheduleApply = nextText => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const value = parseInput(column, nextText);

      if (value !== undefined) ctx.applyFilterRow(column, operation, value);
    }, FILTER_ROW_DELAY);
  };

  const chooseOperation = nextOperation => {
    setMenuAnchor(null);
    ctx.setSelectedOperation(column, nextOperation);

    if (nextOperation === "between" || operation === "between") {
      apply(nextOperation, null);
      return;
    }

    const value = isDateType(column.dataType) ? appliedValue : parseInput(column, text);

    if (value != null && value !== undefined) apply(nextOperation, value);
  };

  const reset = () => {
    setMenuAnchor(null);
    ctx.setSelectedOperation(column, null);
    setText("");
    apply(null, null);
  };

  if (!column.allowFiltering)
    return (
      <th
        className={cellClassName(viewColumn)}
        style={viewColumn.fixed ? { [viewColumn.fixed]: viewColumn.offset } : undefined}
      />
    );

  let editor;

  if (column.dataType === "boolean")
    editor = (
      <Select
        native
        variant="standard"
        disableUnderline
        value={appliedValue == null ? "" : String(appliedValue)}
        onChange={event =>
          apply("=", event.target.value === "" ? null : event.target.value === "true")
        }
        sx={{ flex: "1 1 auto", minWidth: 0, "& select": { py: 0, pl: 0.5 } }}
      >
        <option value="">{ctx.t("gridAll")}</option>
        <option value="true">{column.trueText ?? ctx.t("yes")}</option>
        <option value="false">{column.falseText ?? ctx.t("no")}</option>
      </Select>
    );
  else if (operation === "between")
    editor = (
      <BetweenEditor
        column={column}
        value={appliedValue}
        onApply={value => apply("between", value)}
        locale={ctx.locale}
        t={ctx.t}
      />
    );
  else if (isDateType(column.dataType))
    editor = (
      <DateEditor
        value={appliedValue}
        onChange={value => apply(operation, value)}
        format={ctx.locale?.dateInputFormat}
        delay={FILTER_ROW_DELAY}
      />
    );
  else
    editor = (
      <InputBase
        value={text}
        onChange={event => {
          setText(event.target.value);
          scheduleApply(event.target.value);
        }}
        onKeyDown={event => {
          if (event.key === "Enter") {
            const value = parseInput(column, text);

            if (value !== undefined) apply(operation, value);
          }

          if (event.key === "Escape") {
            setText("");
            apply(operation, null);
          }
        }}
        inputProps={{
          "aria-label": `${ctx.t("filter")} ${column.caption}`,
          inputMode: column.dataType === "number" ? "decimal" : "text",
        }}
        sx={{ flex: "1 1 auto", minWidth: 0 }}
      />
    );

  const active = appliedValue != null && appliedValue !== "";

  return (
    <th
      className={cellClassName(viewColumn)}
      style={viewColumn.fixed ? { [viewColumn.fixed]: viewColumn.offset } : undefined}
      data-col-index={viewColumn.index}
    >
      <div className={`cg-filter-editor${active ? " cg-active" : ""}`}>
        {column.dataType !== "boolean" ? (
          <IconButton
            size="small"
            onClick={event => setMenuAnchor(event.currentTarget)}
            aria-label={ctx.t(OPERATION_LABELS[operation] || "filter")}
            title={ctx.t(OPERATION_LABELS[operation] || "filter")}
            sx={{ p: 0.25, color: active ? "primary.main" : "text.disabled" }}
          >
            <Iconify
              icon={OPERATION_ICONS[operation] || "mdi:magnify"}
              width={16}
            />
          </IconButton>
        ) : null}
        {editor}
        {active &&
        !isDateType(column.dataType) &&
        column.dataType !== "boolean" &&
        operation !== "between" ? (
          <IconButton
            size="small"
            onClick={() => {
              setText("");
              apply(operation, null);
            }}
            aria-label={ctx.t("clear")}
            sx={{ p: 0.25 }}
          >
            <Iconify
              icon="mingcute:close-line"
              width={14}
            />
          </IconButton>
        ) : null}
      </div>
      <Menu
        open={!!menuAnchor}
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
      >
        {operations.map(item => (
          <MenuItem
            key={item}
            selected={item === operation}
            onClick={() => chooseOperation(item)}
            dense
          >
            <Iconify
              icon={OPERATION_ICONS[item] || "mdi:filter-outline"}
              width={18}
              sx={{ mr: 2, flexShrink: 0 }}
            />
            {ctx.t(OPERATION_LABELS[item])}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={reset}
          dense
        >
          <Iconify
            icon="mdi:filter-remove-outline"
            width={18}
            sx={{ mr: 2, flexShrink: 0 }}
          />
          {ctx.t("gridOperationReset")}
        </MenuItem>
      </Menu>
    </th>
  );
}

FilterCell.propTypes = {
  viewColumn: PropTypes.object,
  ctx: PropTypes.object,
};

export default function FilterRow({ viewColumns, ctx }) {
  return (
    <tr className="cg-filter-row">
      {viewColumns.map(viewColumn =>
        viewColumn.kind === "data" ? (
          <FilterCell
            key={viewColumn.key}
            viewColumn={viewColumn}
            ctx={ctx}
          />
        ) : (
          <th
            key={viewColumn.key}
            className={cellClassName(viewColumn)}
            style={viewColumn.fixed ? { [viewColumn.fixed]: viewColumn.offset } : undefined}
          />
        )
      )}
    </tr>
  );
}

FilterRow.propTypes = {
  viewColumns: PropTypes.array,
  ctx: PropTypes.object,
};
